import { create } from "zustand"
import { devtools, persist } from "zustand/middleware"

import { getAccessToken } from "@/api"
import { clearImageSearchHistoryCache } from "@/pages/image-search/historyCache"
import {
  clearPhotoSearchSnapshots,
  invalidatePhotoSearchNavigation,
} from "@/pages/image-search/session"
import type { Tokens, User } from "@/types/api/user"

type LoginState = {
  state: string
  redirect: string
}

interface PermissionUser extends User {
  is_admin: boolean
  is_staff: boolean
  is_pl_admin: boolean
  is_pl_external: boolean
  is_pl_originals: boolean
  is_pl_lensen: boolean
}

type AuthStore = {
  accessToken: string | null
  refreshToken: string | null
  user: PermissionUser | null
  loginState: LoginState | null
  setTokens: (tokens: Tokens) => void
  setAccessToken: (accessToken: string) => void
  setUser: (user: User) => void
  refreshAccessToken: () => Promise<void>
  reset: () => void
  setLoginState: (loginState: LoginState) => void
}

export const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      (setState, getState) => ({
        accessToken: null,
        refreshToken: null,
        user: null,
        loginState: null,
        setTokens: (tokens) =>
          setState({
            accessToken: tokens.access,
            refreshToken: tokens.refresh,
            loginState: null,
          }),
        setAccessToken: (accessToken) => setState({ accessToken }),
        setUser: (user: User) => {
          const permissionUser: PermissionUser = {
            ...structuredClone(user),
            is_admin: user.groups.includes("admin"),
            is_staff: user.groups.includes("staff"),
            is_pl_admin: user.groups.includes("pl_admin"),
            is_pl_external: user.groups.includes("pl_external"),
            is_pl_originals: user.groups.includes("pl_external_o"),
            is_pl_lensen: user.groups.includes("pl_lensen"),
          }
          setState({ user: permissionUser })
        },
        refreshAccessToken: async () => {
          const { refreshToken } = getState()

          if (!refreshToken) {
            return
          }

          const res = await getAccessToken(refreshToken)
          if (res?.status === 200) {
            setState({ accessToken: res.data.access })
          } else {
            const state = getState()
            state.reset()
          }
        },
        reset: () =>
          setState({ accessToken: null, refreshToken: null, user: null, loginState: null }),
        setLoginState: (loginState) => setState({ loginState }),
      }),
      { name: "authStore" }
    )
  )
)

// This also runs while the search page is unmounted on a product or login page.
useAuthStore.subscribe((state, previous) => {
  if (state.user?.id !== previous.user?.id) {
    clearImageSearchHistoryCache()
    if (previous.user) invalidatePhotoSearchNavigation()
    else clearPhotoSearchSnapshots()
  }
})
